import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
	Avatar,
	Box,
	Card,
	CardContent,
	Chip,
	ClickAwayListener,
	Divider,
	Grid,
	InputAdornment,
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	OutlinedInput,
	Paper,
	Popper,
	Stack,
	Switch,
	Typography
} from '@mui/material';

// third-party
import PerfectScrollbar from 'react-perfect-scrollbar';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';
import useAuth from 'hooks/useAuth';

// assets
import { IconLogout, IconSearch, IconSettings, IconUser, IconTimeline } from '@tabler/icons';
import useConfig from 'hooks/useConfig';
import FTESection from './FTESection';
import { returnProfilePicture } from 'utils/utilFunctions';

// ==============================|| PROFILE MENU ||============================== //

const ProfileSection = () => {
	const theme = useTheme();
	const { borderRadius } = useConfig();
	const navigate = useNavigate();

	const [sdm, setSdm] = useState(true);
	const [value, setValue] = useState('');
	const [notification, setNotification] = useState(false);
	const [selectedIndex, setSelectedIndex] = useState(-1);
	const { logout, user, updateSettings } = useAuth();
	const [open, setOpen] = useState(false);
	const [fteOpen, setFTEOpen] = useState(false);

	/**
	 * anchorRef is used on different components and specifying one type leads to other components throwing an error
	 * */
	const anchorRef = useRef(null);
	const handleLogout = async () => {
		try {
			await logout();
		} catch (err) {
			console.error(err);
		}
	};

	const handleClose = (event) => {
		if (anchorRef.current && anchorRef.current.contains(event.target)) {
			return;
		}
		setOpen(false);
	};
	const handleListItemClick = (event, index, route = '') => {
		setSelectedIndex(index);
		handleClose(event);

		if (route && route !== '') {
			navigate(route);
		}
	};
	const handleToggle = () => {
		setOpen((prevOpen) => !prevOpen);
	};

	const handleFTEToggle = () => {
		setFTEOpen((prev) => !prev);
	};

	const prevOpen = useRef(open);
	useEffect(() => {
		if (prevOpen.current === true && open === false) {
			anchorRef.current.focus();
		}

		prevOpen.current = open;
	}, [open]);

	return (
		<>
			<Chip
				sx={{
					height: '48px',
					alignItems: 'center',
					borderRadius: '27px',
					transition: 'all .2s ease-in-out',
					borderColor:
						theme.palette.mode === 'dark'
							? theme.palette.dark.main
							: theme.palette.primary.light,
					backgroundColor:
						theme.palette.mode === 'dark'
							? theme.palette.dark.main
							: theme.palette.primary.light,
					'&[aria-controls="menu-list-grow"], &:hover': {
						borderColor: theme.palette.primary.main,
						background: `${theme.palette.primary.main}!important`,
						color: theme.palette.primary.light,
						'& svg': {
							stroke: theme.palette.primary.light
						}
					},
					'& .MuiChip-label': {
						lineHeight: 0
					}
				}}
				icon={
					<Avatar
						// src={User1}
						sx={{
							...theme.typography.mediumAvatar,
							margin: '8px 0 8px 8px !important',
							cursor: 'pointer'
						}}
						ref={anchorRef}
						aria-controls={open ? 'menu-list-grow' : undefined}
						aria-haspopup='true'
						color='inherit'
						src={returnProfilePicture({ userInfo: user })}
					/>
				}
				label={
					<IconSettings stroke={1.5} size='1.5rem' color={theme.palette.primary.main} />
				}
				variant='outlined'
				ref={anchorRef}
				aria-controls={open ? 'menu-list-grow' : undefined}
				aria-haspopup='true'
				onClick={handleToggle}
				color='primary'
			/>

			<Popper
				placement='bottom'
				open={open}
				anchorEl={anchorRef.current}
				role={undefined}
				transition
				disablePortal
				popperOptions={{
					modifiers: [
						{
							name: 'offset',
							options: {
								offset: [0, 14]
							}
						}
					]
				}}
			>
				{({ TransitionProps }) => (
					<ClickAwayListener onClickAway={handleClose}>
						<Transitions in={open} {...TransitionProps}>
							<Paper>
								{open && (
									<MainCard
										border={false}
										elevation={16}
										content={false}
										boxShadow
										shadow={theme.shadows[16]}
									>
										<Box sx={{ p: 2, pb: 0 }}>
											<Stack>
												<Stack
													direction='row'
													spacing={0.5}
													alignItems='center'
												>
													<Typography variant='h4'>
														Welcome Back,
													</Typography>
													<Typography
														component='span'
														variant='h4'
														sx={{ fontWeight: 400 }}
													>
														{user?.name}
													</Typography>
												</Stack>
												<Typography variant='subtitle2'>
													{user?.permissions?.join(', ')}
												</Typography>
											</Stack>

											<Divider />
										</Box>
										<PerfectScrollbar
											style={{
												height: '100%',
												maxHeight: 'calc(100vh - 250px)',
												overflowX: 'hidden'
											}}
										>
											<Box sx={{ p: 2, pt: 0 }}>
												<Divider />
												<Card
													sx={{
														bgcolor:
															theme.palette.mode === 'dark'
																? theme.palette.dark[800]
																: theme.palette.primary.light,
														my: 2
													}}
												>
													<CardContent>
														<Grid
															container
															spacing={3}
															direction='column'
														>
															<Grid item>
																<Typography variant='subtitle1'>
																	Dashboard Settings
																</Typography>
															</Grid>
															<Grid item>
																<Grid
																	item
																	container
																	alignItems='center'
																	justifyContent='space-between'
																>
																	<Grid item>
																		<Typography variant='subtitle1'>
																			Calendar
																		</Typography>
																	</Grid>
																	<Grid item>
																		<Switch
																			color='primary'
																			checked={user?.calendar}
																			onChange={(e) => {
																				updateSettings(
																					'calendar',
																					e.target
																						?.checked
																				);
																			}}
																			name='calendar'
																			size='small'
																		/>
																	</Grid>
																</Grid>
															</Grid>
															<Grid item>
																<Grid
																	item
																	container
																	alignItems='center'
																	justifyContent='space-between'
																>
																	<Grid item>
																		<Typography variant='subtitle1'>
																			Opportunity Performance
																		</Typography>
																	</Grid>
																	<Grid item>
																		<Switch
																			checked={
																				user?.oppPerformance
																			}
																			onChange={(e) => {
																				updateSettings(
																					'oppPerformance',
																					e.target.checked
																				);
																			}}
																			name='oppPerformance'
																			size='small'
																		/>
																	</Grid>
																</Grid>
															</Grid>
														</Grid>
													</CardContent>
												</Card>
												<Divider />
												<List
													component='nav'
													sx={{
														width: '100%',
														maxWidth: 350,
														minWidth: 300,
														backgroundColor:
															theme.palette.background.paper,
														borderRadius: '10px',
														[theme.breakpoints.down('md')]: {
															minWidth: '100%'
														},
														'& .MuiListItemButton-root': {
															mt: 0.5
														}
													}}
												>
													<ListItemButton
														sx={{ borderRadius: `${borderRadius}px` }}
														selected={selectedIndex === 0}
														onClick={(event) =>
															handleListItemClick(
																event,
																0,
																'/user/account-profile/profile1'
															)
														}
													>
														<ListItemIcon>
															<IconSettings
																stroke={1.5}
																size='1.3rem'
															/>
														</ListItemIcon>
														<ListItemText
															primary={
																<Typography variant='body2'>
																	Account Settings
																</Typography>
															}
														/>
													</ListItemButton>

													<ListItemButton
														sx={{ borderRadius: `${borderRadius}px` }}
														selected={selectedIndex === 1}
														onClick={handleFTEToggle}
													>
														<ListItemIcon>
															<IconTimeline
																stroke={1.5}
																size='1.3rem'
															/>
														</ListItemIcon>
														<ListItemText
															primary={
																<Grid
																	container
																	spacing={1}
																	justifyContent='space-between'
																>
																	<Grid item>
																		<Typography variant='body2'>
																			FTE Allocations
																		</Typography>
																	</Grid>
																	<Grid item>
																		<Chip
																			label={
																				user?.ftes.length ||
																				0
																			}
																			size='small'
																			sx={{
																				bgcolor:
																					theme.palette
																						.mode ===
																					'dark'
																						? theme
																								.palette
																								.dark
																								.dark
																						: theme
																								.palette
																								.warning
																								.dark,
																				color: theme.palette
																					.background
																					.default
																			}}
																		/>
																	</Grid>
																</Grid>
															}
														/>
													</ListItemButton>
													{fteOpen ? <FTESection /> : null}

													<ListItemButton
														sx={{ borderRadius: `${borderRadius}px` }}
														selected={selectedIndex === 4}
														onClick={handleLogout}
													>
														<ListItemIcon>
															<IconLogout
																stroke={1.5}
																size='1.3rem'
															/>
														</ListItemIcon>
														<ListItemText
															primary={
																<Typography variant='body2'>
																	Logout
																</Typography>
															}
														/>
													</ListItemButton>
												</List>
											</Box>
										</PerfectScrollbar>
									</MainCard>
								)}
							</Paper>
						</Transitions>
					</ClickAwayListener>
				)}
			</Popper>
		</>
	);
};

export default ProfileSection;
